/* eslint-disable no-unused-vars */
import { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Pen, ErrorIcon } from 'assets/icons';
import { IconButton, Buttons, FloatingPopUp, Label, Bid, Loader } from 'components';
import { campaignReportsAPI } from 'services/campaignReports.service';
import { campaignsManageAPI } from 'services/campaignsManage.service';
import { isAdminSelector } from 'store/selectors';
import { showPolipop, isBidErrorShown, isInvalidBid, trimDecimal } from 'utils';

import * as S from './ChangeBudget.styled';

/**
 * UA Budget Validation - Build error message for an active backend rule.
 * Mirrors the messages used in the campaign edit form for consistency.
 * Only invoked when the rule is active ("CPI" | "Placements" | "Broad").
 * @see JIRA: UA Campaign Budget Validation
 */
const getBudgetRuleErrorMessage = (rule, minimum) => {
  const formattedMinimum = Number.isInteger(minimum) ? minimum.toString() : minimum.toFixed(2);

  switch ((rule || '').toLowerCase()) {
    case 'cpi':
      return `Budget must be at least 10x the Target CPI ($${formattedMinimum}).`;
    case 'broad':
      return `Broad campaigns (All Placements) require a minimum budget of $${formattedMinimum}.`;
    case 'placements':
    default:
      return `Based on your targeting (Placements x Countries), the minimum budget is $${formattedMinimum}.`;
  }
};

/**
 * Inline budget edit component for Campaign Reports page.
 *
 * UA Budget Validation (budget-only edit):
 * - Sends only campaignId (cmp_id); backend uses saved campaign data (case 3).
 * - When the entered budget is below the returned minimum, the Ok button is
 *   disabled and a red inline error message is shown.
 *
 * @see JIRA: UA Campaign Budget Validation
 */
export const ChangeBudget = ({ budgetValue }) => {
  const [isOpen, setIsOpen] = useState(false);

  const triggerComponentRef = useRef(null);

  const closeAction = () => {
    setIsOpen(false);
  };

  return (
    <FloatingPopUp
      durationValue={50}
      isArrow
      offsetValue={10}
      defaultPlacement={FloatingPopUp.placements.top}
      isOpen={isOpen}
      triggerStyle={{ zIndex: isOpen && 2100 }}
      closeAction={closeAction}
      popUpComponent={<TooltipContent closeAction={closeAction} budgetValue={budgetValue} />}
      triggerComponent={
        <IconButton.Micro
          onClick={() => setIsOpen((prev) => !prev)}
          ref={triggerComponentRef}
          icon={Pen}
          type={IconButton.btnTypes.tertiary}
          isPressed={isOpen}
        />
      }
    />
  );
};

const TooltipContent = ({ closeAction, budgetValue }) => {
  const { cmp_id } = useParams();
  const isAdmin = useSelector(isAdminSelector);
  const currentCampaignObj = useSelector(
    (state) => state?.general?.mainCampaignObject?.currentCampaignObj,
  );
  const [budget, setBudget] = useState(budgetValue);

  const [updateCampaignBudget, result] = campaignReportsAPI.useUpdateDailyBudgetMutation();
  const [getMinBudget, { data: budgetValidation }] =
    campaignsManageAPI.useGetMinimumRequiredBudgetMutation();

  const isCPI = currentCampaignObj?.bidType === 3;

  const maxDailyBudget = isCPI ? window.cpi_max_daily_budget : window.max_daily_budget;
  const minDailyBudget = isAdmin ? window.min_daily_budget_admin : window.min_daily_budget;

  useEffect(() => {
    if (cmp_id) {
      getMinBudget(cmp_id);
    }
  }, [cmp_id]);

  // Legacy campaigns: backend returns applicableRule = 'LegacyMinimum' (deterministic);
  // 'None' is also treated as no-rule. Both fall back to the original error messages.
  const minimumBudget = budgetValidation?.minimumBudget || 0;
  const applicableRule = budgetValidation?.applicableRule || 'None';
  const isRuleActive =
    minimumBudget > 0 && applicableRule !== 'None' && applicableRule !== 'LegacyMinimum';
  const budgetRuleError = isRuleActive && Number(budget || 0) < minimumBudget;

  const isError = isBidErrorShown(budget, minDailyBudget, maxDailyBudget) || budgetRuleError;
  const isDisabled =
    isInvalidBid(budget, budgetValue, minDailyBudget, maxDailyBudget) || budgetRuleError;

  const errorMessage = budgetRuleError
    ? getBudgetRuleErrorMessage(applicableRule, minimumBudget)
    : budget < minDailyBudget
    ? `Budget must be at least $${minDailyBudget}`
    : budget > maxDailyBudget
    ? 'Your budget is too high'
    : '';

  const handleBudgetChange = (e) => {
    setBudget(trimDecimal(e.target.value));
  };

  const handleBlur = (e) => {
    const currentBudget = e.target.value;
    if (currentBudget.startsWith('.')) {
      setBudget(trimDecimal('0' + currentBudget));
    }
  };

  const handleSave = async () => {
    const budgetToSave = budget.toString().startsWith('.') ? trimDecimal('0' + budget) : budget;
    if (isInvalidBid(budget, budgetValue, minDailyBudget, maxDailyBudget) || budgetRuleError)
      return;

    const res = await updateCampaignBudget({
      campaignId: cmp_id,
      dailyBudget: budgetToSave,
    });

    if (res?.data?.success) closeAction();
  };

  useEffect(() => {
    if (budgetValue !== budget) {
      setBudget(budgetValue);
    }
  }, [budgetValue]);

  useEffect(() => {
    if (result.isSuccess || result.isError) {
      showPolipop(result);
    }
  }, [result]);

  return (
    <S.PopUpBox>
      {result.isLoading && <Loader.Global delay={0} />}
      <S.FlexWrap>
        <Label text='Daily Budget:' variant='label' style={{ height: '40px' }} />
        <Bid.Big
          value={budget}
          onChange={handleBudgetChange}
          onBlur={handleBlur}
          withError={false}
        />
      </S.FlexWrap>
      <S.ErrorWrap style={{ opacity: errorMessage && isError ? '1' : '0' }}>
        <ErrorIcon />
        {errorMessage}
      </S.ErrorWrap>
      <S.FlexWrap>
        <Buttons.SecondaryNormal
          id='closeModalId'
          style={{ width: 'calc(50% - 5px)' }}
          onClick={closeAction}
        >
          Cancel
        </Buttons.SecondaryNormal>
        <Buttons.PrimaryNormal
          id='saveModalId'
          style={{ width: 'calc(50% - 5px)' }}
          onClick={handleSave}
          isDisabled={isDisabled}
        >
          Ok
        </Buttons.PrimaryNormal>
      </S.FlexWrap>
    </S.PopUpBox>
  );
};
